import { useCallback, useEffect, useRef, useState } from 'react';
import { Quote, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { testimonialsConfig } from '@/config';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { cn } from '@/lib/utils';
import { useCollection, useConfig } from '@/lib/siteData';
import { imageVariants, withWebp } from '@/lib/imageSrc';

interface TestimonialsSectionConfig {
  heading?: string;
  body?: string;
  visible?: boolean;
}
const defaultTestimonialsSection: TestimonialsSectionConfig = {};

const AUTOPLAY_MS = 5000;

interface TestimonialRow {
  id: string;
  name: string;
  role: string;
  quote: string;
  image?: string | null;
  sortOrder?: number;
  visible?: boolean;
}

export function Testimonials({ index }: { index?: number } = {}) {
  const testimonialsSection = useConfig<TestimonialsSectionConfig>('testimonials_section', defaultTestimonialsSection);
  const { data: apiTestimonials } = useCollection<TestimonialRow>('testimonials');
  // Map API rows to the frontend's expected shape (title comes from role)
  const testimonials = apiTestimonials.length > 0
    ? apiTestimonials
        .filter((t) => t.visible !== false)
        .map((t) => ({ name: t.name, title: t.role, quote: t.quote, image: t.image ?? '' }))
    : testimonialsConfig.testimonials;
  const prefersReducedMotion = useReducedMotion();
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const track = trackRef.current;
      if (!track) return;
      const card = track.children[index] as HTMLElement | undefined;
      if (!card) return;
      const left = card.offsetLeft - track.offsetLeft;
      track.scrollTo({ left, behavior: prefersReducedMotion ? 'auto' : behavior });
    },
    [prefersReducedMotion]
  );

  const goTo = useCallback(
    (index: number) => {
      if (testimonials.length === 0) return;
      const normalized = (index + testimonials.length) % testimonials.length;
      setActiveIndex(normalized);
      scrollToIndex(normalized);
    },
    [testimonials.length, scrollToIndex]
  );

  // Track scroll position → derive active index (keeps dot indicator accurate on swipe/drag)
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const children = Array.from(track.children) as HTMLElement[];
        if (children.length === 0) return;
        const center = track.scrollLeft + track.clientWidth / 2;
        let nearest = 0;
        let nearestDist = Infinity;
        children.forEach((child, i) => {
          const childCenter = child.offsetLeft + child.clientWidth / 2 - track.offsetLeft;
          const dist = Math.abs(childCenter - center);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
        });
        setActiveIndex(nearest);
      });
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Autoplay (skip when reduced motion, paused, or tab hidden)
  useEffect(() => {
    if (prefersReducedMotion || isPaused || userPaused || testimonials.length <= 1) return;
    const id = window.setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % testimonials.length;
        scrollToIndex(next);
        return next;
      });
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [prefersReducedMotion, isPaused, userPaused, testimonials.length, scrollToIndex]);

  if (testimonialsSection.visible === false) return null;
  if (!testimonials || testimonials.length === 0) return null;

  return (
    <section
      id="testimonials"
      className="w-full py-16 md:py-20 bg-surface-elevated border-t border-surface-border-gray"
    >
      <div className="w-full max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center mb-10 md:mb-12">
          <span className="font-mono-label text-xs uppercase tracking-[0.2em] text-text-light">
            {index != null && <span className="text-accent-blue mr-1">{String(index).padStart(2, '0')} — </span>}
            TESTIMONIALS
          </span>
          <h2 className="font-geist-mono text-3xl md:text-[2.5rem] font-medium text-text-primary mt-3 leading-[1.1] tracking-tight">
            {testimonialsSection.heading}
          </h2>
          <p className="font-body text-base text-text-gray mt-3 max-w-xl mx-auto whitespace-pre-line">
            {testimonialsSection.body || 'Feedback from partners, clients, and collaborators.'}
          </p>
        </div>

        <div
          className="relative"
          role="region"
          aria-roledescription="carousel"
          aria-label="Testimonials"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocus={() => setIsPaused(true)}
          onBlur={() => setIsPaused(false)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              goTo(activeIndex + 1);
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              goTo(activeIndex - 1);
            }
          }}
        >
          <div
            ref={trackRef}
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-6 px-6 lg:mx-0 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-live={isPaused ? 'polite' : 'off'}
          >
            {testimonials.map((t, idx) => {
              const initials = t.name
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((w) => w[0]?.toUpperCase() ?? '')
                .join('');
              return (
                <figure
                  key={`${t.name}-${idx}`}
                  aria-hidden={idx !== activeIndex}
                  className="flex flex-col p-6 rounded-xl border border-surface-border-gray bg-surface-elevated snap-center shrink-0 w-[85%] sm:w-[60%] md:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)]"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${idx + 1} of ${testimonials.length}`}
                >
                  <Quote className="w-5 h-5 text-accent-blue mb-4" />
                  <blockquote className="font-body text-sm text-text-gray leading-relaxed mb-5 flex-1">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="flex items-center gap-3">
                    {t.image ? (
                      withWebp(
                        // 40px avatar — an explicit `sizes` is what stops the
                        // browser assuming 100vw and fetching the 1200w variant.
                        imageVariants(t.image, '40px'),
                        <img
                          src={t.image}
                          alt=""
                          width={40}
                          height={40}
                          className="w-10 h-10 rounded-full object-cover border border-surface-border-gray flex-shrink-0"
                          loading="lazy"
                          decoding="async"
                        />,
                      )
                    ) : (
                      <div
                        aria-hidden="true"
                        className="w-10 h-10 rounded-full bg-accent-blue/10 text-accent-blue font-heading text-sm font-semibold flex items-center justify-center flex-shrink-0"
                      >
                        {initials || '–'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-heading text-sm font-semibold text-text-primary truncate">
                        {t.name}
                      </div>
                      <div className="font-body text-xs text-text-light mt-0.5 truncate">
                        {t.title}
                      </div>
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>

          {/* Controls */}
          <div className="mt-6 flex items-center justify-center gap-4">
            {!prefersReducedMotion && testimonials.length > 1 && (
              <button
                type="button"
                onClick={() => setUserPaused((p) => !p)}
                aria-pressed={userPaused}
                aria-label={userPaused ? 'Resume automatic rotation' : 'Pause automatic rotation'}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-surface-border-gray bg-surface-elevated text-text-gray transition-colors hover:border-accent-blue hover:text-accent-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
              >
                {userPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </button>
            )}
            <button
              type="button"
              onClick={() => goTo(activeIndex - 1)}
              aria-label="Previous testimonial"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-surface-border-gray bg-surface-elevated text-text-gray transition-colors hover:border-accent-blue hover:text-accent-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {/* Windowed dot pagination — only render dots inside a 5-dot window
                centered on the active index. With 20+ testimonials a flat row
                of dots is too cramped to tap. Hidden on mobile (arrows suffice). */}
            <div
              className="hidden sm:flex items-center gap-2"
              role="tablist"
              aria-label="Select testimonial"
            >
              {(() => {
                const total = testimonials.length;
                const window = 5;
                const half = Math.floor(window / 2);
                let start = Math.max(0, activeIndex - half);
                const end = Math.min(total, start + window);
                start = Math.max(0, end - window);
                const indices: number[] = [];
                for (let i = start; i < end; i += 1) indices.push(i);
                const showStartEllipsis = start > 0;
                const showEndEllipsis = end < total;
                return (
                  <>
                    {showStartEllipsis && (
                      <span aria-hidden="true" className="text-text-light text-xs">…</span>
                    )}
                    {indices.map((idx) => (
                      <button
                        key={idx}
                        type="button"
                        role="tab"
                        aria-selected={idx === activeIndex}
                        aria-label={`Go to testimonial ${idx + 1}`}
                        onClick={() => goTo(idx)}
                        className={cn(
                          'h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue',
                          idx === activeIndex
                            ? 'w-6 bg-accent-blue'
                            : 'w-2 bg-surface-border-gray hover:bg-text-light'
                        )}
                      />
                    ))}
                    {showEndEllipsis && (
                      <span aria-hidden="true" className="text-text-light text-xs">…</span>
                    )}
                  </>
                );
              })()}
            </div>
            {/* N of M counter — visible on all screen sizes. */}
            <span className="text-xs font-mono-label text-text-light" aria-live="polite">
              {activeIndex + 1} / {testimonials.length}
            </span>

            <button
              type="button"
              onClick={() => goTo(activeIndex + 1)}
              aria-label="Next testimonial"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-surface-border-gray bg-surface-elevated text-text-gray transition-colors hover:border-accent-blue hover:text-accent-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
